import * as THREE from 'three';
import { BLOCK_CAPS, Track, type RouteBlock } from '@vrlearning/lesson-schema';

import { random } from '../../look/noise.js';
import type { StatefulBlock } from './features.js';
import type { PartRegistry } from './parts.js';

// The path maths live in lesson-schema since ADR-054 (decision 10); re-exported for this block's callers.
export { Track, routePoint } from '@vrlearning/lesson-schema';

/**
 * The route block (V2): the part named `part…` hidden and drawn as `count` instanced copies going along
 * `path` at their own speeds; `level` 0–1 sets how many are out (`present`), each growing in as it comes;
 * on an open path they loop from the start and grow and shrink within `fade` metres of its ends.
 */
export function buildRouteBlock(
  root: THREE.Object3D,
  block: RouteBlock,
  parts: PartRegistry,
): StatefulBlock & { tick(elapsed: number): void } {
  const count = Math.min(block.count, BLOCK_CAPS.route);
  const next = random(block.seed);
  const track = new Track(block.path, block.closed);
  const total = track.total;
  const here = { x: 0, z: 0, heading: 0 };
  const gap = block.spacing > 0 ? block.spacing : total / count;
  const movers = Array.from({ length: count }, (_, i) => ({
    offset: i * gap + next() * Math.min(8, gap / 4),
    speed: block.speed[0] + next() * (block.speed[1] - block.speed[0]),
  }));
  let level = block.level;
  // One instanced mesh for each mesh of the part (each is a draw call, as the cost counts).
  let copies: THREE.InstancedMesh[] = [];
  let sources: THREE.Mesh[] = [];
  const matrix = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const size = new THREE.Vector3();
  const at = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  parts.when([block.part], (meshes) => {
    if (copies.length > 0) return;
    sources = meshes;
    copies = meshes.map((mesh, k) => {
      mesh.visible = false;
      // The part's shared copies of its materials: what kleur does to the part shows on the copies too.
      parts.materialsOf(mesh);
      const instanced = new THREE.InstancedMesh(mesh.geometry, mesh.material, count);
      instanced.name = k === 0 ? block.id : `${block.id}-${k}`;
      instanced.frustumCulled = false;
      instanced.raycast = (): void => {};
      root.add(instanced);
      return instanced;
    });
  });
  const tick = (elapsed: number): void => {
    if (copies.length === 0) return;
    // A kleur block may have swapped the part's materials (its light look): the copies follow.
    for (let k = 0; k < copies.length; k++) {
      const own = parts.materialsOf(sources[k]!);
      copies[k]!.material = own.length === 1 ? own[0]! : own;
    }
    const out = block.present[0] + (block.present[1] - block.present[0]) * level;
    for (let i = 0; i < count; i++) {
      const mover = movers[i]!;
      const travelled = mover.offset + mover.speed * elapsed;
      const d = block.closed ? travelled : ((travelled % total) + total) % total;
      track.at(d, here);
      const ends =
        block.closed || block.fade <= 0
          ? 1
          : THREE.MathUtils.smoothstep(Math.min(d, total - d), 0, block.fade);
      const grown = THREE.MathUtils.smoothstep(out - i, 0, 1) * ends;
      q.setFromAxisAngle(up, here.heading);
      matrix.compose(at.set(here.x, block.y, here.z), q, size.setScalar(grown));
      for (const instanced of copies) instanced.setMatrixAt(i, matrix);
    }
    for (const instanced of copies) instanced.instanceMatrix.needsUpdate = true;
  };
  return {
    tick,
    set(setting, value) {
      if (setting === 'level') level = value as number;
    },
  };
}
